import {
    Component,
    computed,
    Input,
    OnChanges,
    OnInit,
    output,
    signal,
    SimpleChanges,
    WritableSignal
} from '@angular/core';
import {abydosFormComponents, CrudFormComponent} from '@abydos-angular/form';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Location} from '@angular/common';
import {ApplicationVO} from '../../rest-api/application/store/application.interfaces';
import {ApplicationState} from '../../rest-api/application/store/application.reducers';
import {ApplicationFacade} from '../../rest-api/application/store/application.facade';
import * as dateFns from 'date-fns';
import {cloneDeep} from 'lodash';
import {coalesce, FetchRequest, isNullOrUndefined, RestResponse} from '@abydos-angular/core';
import {isNotNullOrUndefined} from 'codelyzer/util/isNotNullOrUndefined';
import {EventFacade} from '../../event/store/event.facade';
import {ActivityVO} from '../../activity/store/activity.interfaces';
import {EventListVO, EventVO} from '../../event/store/event.interfaces';
import {AuthFacade} from '@abydos-angular/rest-api';
import {AtmosphereService} from '../../service/atmosphere/atmosphere.service';
import {ConfirmationService, MessageService} from 'primeng/api';
import {InfoMessage} from '@abydos-angular/primeng';
import {Toast} from 'primeng/toast';

type UserFormGroupType = {
    id: FormControl<number | null | undefined>,
    name: FormControl<string | null | undefined>,
    displayName: FormControl<string | null | undefined>
    email: FormControl<string | null | undefined>,
    neptunIdentifier: FormControl<string | null | undefined>,
}

type PartnerFormGroupType = {
    id: FormControl<number | null | undefined>,
    name: FormControl<string | null | undefined>,
    email: FormControl<string | null | undefined>,
}
type ActivityFormGroupType = {
    id: FormControl<number | null | undefined>,
    name: FormControl<string | null | undefined>,
    cancelHoursBeforeEvent: FormControl<number | null | undefined>,
    partnerClient: FormGroup<PartnerFormGroupType>
}

type EventFormGroupType = {
    id: FormControl<number | null | undefined>,
    slotId: FormControl<string | null | undefined>
    title: FormControl<string | null | undefined>,
    reserved: FormControl<string | number | null | undefined>
    numberOfApplicants: FormControl<string | number | null | undefined>,
    activity: FormGroup<ActivityFormGroupType>,
    address: FormControl<string | null | undefined>,
    start: FormControl<Date | null | undefined>,
    end: FormControl<Date | null | undefined>
}

type ApplicationForm = {
    id: FormControl<string | null | undefined>,
    user: FormGroup<UserFormGroupType>, // A hallgató
    event: FormGroup<EventFormGroupType>, // Az esemény, amin részt vett
    eventId: FormControl<number | null | undefined>,
    plannedStart: FormControl<Date | null | undefined>, // Konkrét időpont kezdete, amire jelentkezik
    plannedEnd: FormControl<Date | null | undefined>, // Konkrét időpont vége, ameddig jelentkezik
    start: FormControl<string | null | undefined>, // Konkrét időpont kezdete, amikortól megjelent
    end: FormControl<string | null | undefined>, // Konkrét időpont vége, ameddig maradt
    approved: FormControl<string | null | undefined>, // Megerősítés, hogy ténylegesen elszámolhatja a hallgató az óráit
    partnerDescription: FormControl<string | null | undefined>, // tetszőleges megjegyzés, amit a partner írhat
    adminDescription: FormControl<string | null | undefined>, // tetszőleges megjegyzés, amit az admin írhat
}

@Component({
    selector: 'app-application-edited-data',
    templateUrl: './application-edited-data.component.html',
    standalone: true,
    imports: [
        abydosFormComponents,
        Toast,
    ],
    providers: [AtmosphereService]
})
export class ApplicationEditedDataComponent extends CrudFormComponent<ApplicationVO, ApplicationState> implements OnInit, OnChanges {

    public selectItems = this.applicationFacade.selectItems;
    fullTime = true;
    public currentStep = 1;
    @Input() applicationView = false;
    @Input() selectedEvent?: EventVO;
    @Input() selectedActivity?: ActivityVO;
    applicationSaved = output()
    acceptTerms = false;
    eventList: EventVO[] = [];
    // a datepicker komponensben nem csak az aktuális hónap, hanem az előző hónap utolsó és következő hónap első pár napja is látszódik
    beginningOfMonth = dateFns.addWeeks(dateFns.startOfMonth(new Date()), -1);
    endOfMonth = dateFns.addWeeks(dateFns.endOfMonth(new Date()), 1);
    currentYear = dateFns.getYear(new Date())

    availableTimesSignal: WritableSignal<EventVO[]> = signal([])

    partTimeOptions: any[] = [
        {label: '10:00 - 13:00', value: 1},
        {label: '13:00 - 16:00', value: 2},
        {label: '16:00 - 19:00', value: 3}
    ]
    datesWithoutEvent: Date[] = [];

    userFormGroup?: FormGroup<UserFormGroupType>;
    typedForm?: FormGroup<ApplicationForm>;

    constructor(private formBuilder: FormBuilder, public applicationFacade: ApplicationFacade, private eventFacade: EventFacade, private authFacade: AuthFacade,
                _location: Location, private atmosphereService: AtmosphereService, private messageService: MessageService) {
        super(applicationFacade, _location);
    }

    protected initForm(): any {
        this.userFormGroup = this.formBuilder.group({
            id: new FormControl(),
            name: new FormControl(),
            displayName: new FormControl(),
            email: new FormControl(),
            neptunIdentifier: new FormControl(),
        }, {});
        this.form = this.formBuilder.group({
            id: new FormControl(),
            user: this.userFormGroup, // A hallgató
            eventId: new FormControl(),
            event: this.formBuilder.group({
                id: new FormControl(),
                slotId: new FormControl(null, Validators.required),
                title: new FormControl(),
                reserved: new FormControl(),
                numberOfApplicants: new FormControl(),
                address: new FormControl(),
                start: new FormControl(),
                end: new FormControl(),
                activity: this.formBuilder.group({
                    id: new FormControl(),
                    name: new FormControl(),
                    cancelHoursBeforeEvent: new FormControl(),
                    partnerClient: this.formBuilder.group({
                        id: new FormControl(),
                        name: new FormControl(),
                        email: new FormControl(),
                    })
                })
            }, {}), // Az esemény, amin részt vett
            plannedStart: new FormControl(), // Konkrét időpont kezdete, amire jelentkezik
            plannedEnd: new FormControl(), // Konkrét időpont vége, ameddig jelentkezik
            start: new FormControl(), // Konkrét időpont kezdete, amikortól megjelent
            end: new FormControl(), // Konkrét időpont vége, ameddig maradt
            approved: new FormControl(), // Megerősítés, hogy ténylegesen elszámolhatja a hallgató az óráit
            partnerDescription: new FormControl(), // tetszőleges megjegyzés, amit a partner írhat
            adminDescription: new FormControl() // tetszőleges megjegyzés, amit az admin írhat
        }) as FormGroup<ApplicationForm>;
        this.typedForm = this.form;
        this.userFormGroup = this.typedForm.controls.user;
        this.addValueChangedListener(this.typedForm, ['plannedStart'], (value: Date, fieldName) => {
            if (isNotNullOrUndefined(value)) {
                this.readAvailableEventByDate(value);
            }
        });
    }

    override ngOnInit() {
        super.ngOnInit();
        this.applicationFacade.initSelectItems();
    }

    protected override convertDataToForm(data: ApplicationVO): any {
        const ret = cloneDeep(super.convertDataToForm(data));
        if (isNotNullOrUndefined(ret.user)) {
            const neededFields = ['id', 'name', 'email', 'neptunIdentifier', 'displayName'];
            ret.user = this.filterObjectFieldNames(ret.user, neededFields)
        }
        if (isNotNullOrUndefined(ret.event)) {
            this.selectedEvent = ret.event;
            this.selectedActivity = ret.event.activity;
            ret.eventId = ret.event.id;
            this.beginningOfMonth = dateFns.addWeeks(dateFns.startOfMonth(ret.plannedStart!), -1);
            this.endOfMonth = dateFns.addWeeks(dateFns.endOfMonth(ret.plannedEnd!), 1);
        }
        return ret;
    }

    protected override getNewDefault(): ApplicationVO {
        const ret: ApplicationVO = {};
        const neededFields = ['id', 'name', 'email', 'neptunIdentifier', 'displayName'];
        if (isNotNullOrUndefined(this.authFacade.currentUserInfo)) {
            ret.user = {
                    ///...this.authFacade.currentUserInfo,
                    ...this.filterObjectFieldNames(this.authFacade.currentUserInfo! as any, neededFields),
                    neptunIdentifier: this.authFacade.currentUserInfo?.extends.neptunIdentifier
            } as ApplicationVO;
        }

        if (isNotNullOrUndefined(this.selectedEvent)) {
            ret.event = {...this.selectedEvent};
            ret.plannedStart = this.selectedEvent?.start;
            ret.plannedEnd = this.selectedEvent?.end;
        }
        return ret;
    }

    readAvailableEventByDate(value?: Date) {
        if (isNullOrUndefined(this.selectedActivity)) {
            return;
        }
        const request = new FetchRequest<any>()
        request.criteria = {
            baseCriteria: [
                {fieldName: 'activity.id', value: this.selectedActivity!.id, operator: 'equals'},
                {fieldName: 'start', operator: 'dateBetween',
                    // ha editMode-ban van, és régebbi dátumról van szó, akkor hagyni kell hogy azt lerkéje, egyébként nem kéérünk le régebbi dátumokat, mert úgysem választható
                    min: dateFns.isBefore(this.beginningOfMonth, dateFns.startOfDay(new Date())) && !this.applicationFacade.isInEditMode() ? dateFns.startOfDay(new Date()) : this.beginningOfMonth,
                    max: this.endOfMonth}
            ],
            representation: 'view'
        }
        request.operationId = 'timetable';
        this.eventFacade.list(request, (result: RestResponse) => {
            const start = this.beginningOfMonth;
            const end = this.endOfMonth;
            if (isNotNullOrUndefined(result.getData())) {
                this.eventList = result.getData()!;
                this.datesWithoutEvent = this.getAllDatesBetween(start, end).filter(d => {
                    // ha editModeban vagyunk akkor lekértük az egész hónapot arra az esetre, ha múltbéli eseményünk volt, viszont ezek a naptáron már nem kell hogy megjelenjenek
                        return !this.eventList?.map(event => new Date(event.start!)).some(ad => this.datesAreEqual(ad, d)) || dateFns.isBefore(d, new Date())
                    }
                );
            }
            if (isNotNullOrUndefined(value)) {
                this.dateSelected(value!);
            }
        })
    }

    datesAreEqual(d1: Date, d2: Date): boolean {
        return d1.getFullYear() === d2.getFullYear()
            && d1.getMonth() === d2.getMonth()
            && d1.getDate() === d2.getDate();
    }

    getAllDatesBetween(start: Date, end: Date): Date[] {
        const dates: Date[] = [];
        const curr = new Date(start);
        while (curr <= end) {
            dates.push(new Date(curr));
            curr.setDate(curr.getDate() + 1);
        }
        return dates;
    }

    dateSelected(date: Date) {
        const testAvailableTimes: EventVO[] = [];
        this.unsubscribe();
        this.eventList.filter(event => dateFns.isSameDay(event.start!, date))
            .map(event => {
                return {
                    ...event,
                    label: this.makeEventTimeLabel(event), // összeállítjuk a label az időpontból és a jelentkezésekből
                    value: event.id,
                    disabled: (event.numberOfApplicants <= event.reserved && this.selectedEvent?.slotId !== event.slotId)
                        || !this.canApply(event) // ellenőrizzük, nem vagyunk-e a legmondási határidőn beül
                }
            })
            // azokra az eventekre iratkozunk fel, amik megjelennek a választó listában, így azoknak már a labelben is látszódni fog,
            // hogyha új foglalás történik rájuk
            .forEach((event) => {
                testAvailableTimes.push(event);
                this.atmosphereService.subscribe(`https://booking4k.unideb.hu/rest/atmosphere/application/${event.id}`,
                    ( socketMessage ) => {
                        this.setNewReservedNumber(JSON.parse(socketMessage.responseBody))
                    })
            })
        this.availableTimesSignal.set(testAvailableTimes);
        if (isNotNullOrUndefined(this.typedForm?.controls.event.get('slotId')?.value)) {
            if (!this.availableTimesSignal().map(event => event.slotId).includes(this.typedForm?.controls.event.get('slotId')?.value!)) {
                this.typedForm!.controls.event.reset();
                this.typedForm!.controls.eventId.reset();
                this.typedForm!.controls.plannedEnd.reset();
                this.typedForm!.controls.event.controls.slotId.reset();
            }
        }
    }

    eventTimeSelected(eventList: EventListVO[], selectedEventId: string | null) {
        if (isNotNullOrUndefined(selectedEventId)) {
            const found = eventList.find(event => event.slotId === selectedEventId);
            if (isNotNullOrUndefined(found)) {
                this.typedForm!.controls.event.patchValue({...found});
                this.typedForm!.controls.plannedStart.setValue(found?.start);
                this.typedForm!.controls.plannedEnd.setValue(found?.end);
                this.typedForm!.controls.eventId.setValue(found?.id);
                this.dialog!.newDialogTitle = this.makeDialogTitle(found)
            } else {
                this.typedForm!.controls.event.reset();
                this.typedForm!.controls.plannedEnd.reset();
                this.typedForm!.controls.eventId.reset();
            }
        } else {
            this.typedForm!.controls.event.reset();
            this.typedForm!.controls.plannedEnd.reset();
            this.typedForm!.controls.eventId.reset();
        }
    }

    dateIntervalToString(startDate?: Date | null, endDate?: Date | null) {
        if (startDate && endDate) {
            return dateFns.format(startDate, 'yyyy.MM.dd HH:mm') + ' - ' + dateFns.format(endDate, 'HH:mm');
        } else {
            return '';
        }
    }

    override ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        if ( 'selectedActivity' in changes ) {
            // Új esetén így kapunk paramétert
            this.readAvailableEventByDate( this.selectedEvent?.start );
        }
    }

    filterObjectFieldNames<T extends object, K extends keyof T>(object: T, fieldNames: K[]): Pick<T, K> {
        const result = {} as Pick<T, K>;
        for (const field of fieldNames) {
            if (field in object) {
                result[field] = object[field];
            }
        }
        return result;
    }

    protected override doSave(payload: any, metadata: any): string | null {
        // mentés előtt ellenőrizzük, hogy a van-e hely, mert ez a jelentkezés kezdete óta változhatott
        if (payload.event.reserved >= payload.event.numberOfApplicants) {
            this.messageService.add({
                severity: 'error',
                summary: 'Esemény megtelt!',
                detail: 'Az esemény által megadott létszám elérte a maximumot, kérjük válasszon egy másik időpontot!',
                life: 3000,
            });
            return '';
        } else {
            return super.doSave(payload, metadata);
        }
    }

    log() {
        // console.log(this.typedForm?.value);
    }

    override isReadOnly(): boolean {
        return !this.applicationCanChanged();  // ha az esemény kezdete már belül van a lemondási határidőn
    }


    private unsubscribe() {
        this.atmosphereService.unsubscribe();
        // this.atmosphereService.unsubscribeFromUrl(`https://booking4k.unideb.hu/rest/atmosphere/application/${this.editedData?.id}`);
    }

    makeEventTimeLabel(event: EventVO) {
        if (this.isReadOnly()) {
            return dateFns.format(event.start!, "HH:mm") + ' - ' + dateFns.format(event.end!, "HH:mm");
        } else {
            return dateFns.format(event.start!, "HH:mm") + ' - ' + dateFns.format(event.end!, "HH:mm") + ' '
                + event.reserved + '/' + event.numberOfApplicants + ' '
                + (dateFns.differenceInHours(event.start!, new Date()) < coalesce(event.activity.cancelHoursBeforeEvent, 48) ? ' JELENTKEZÉSI HATÁRIDŐ LETELT!' : '')
        }
    }

    setNewReservedNumber(application: any) {
        let newArray: any[] = []
        const newEventData: EventVO = {...this.availableTimesSignal().find(event => event.slotId === application.slotId), reserved: application.reserved};
        // kivesszük a régi értéket, és betesszük a újazt a frissített foglalási adatokkal
        newArray = this.availableTimesSignal().filter(event => event.slotId !== application.slotId);
        newArray.push({...newEventData, label: this.makeEventTimeLabel(newEventData)});
        this.availableTimesSignal.set(newArray)
        if (application.slotId === this.typedForm?.controls.event.controls.slotId.value) {
            // ha az éppen kiválasztott eseményre érkezett foglalás akkor frissíteni kell a formot, és a title-t is
            this.dialog!.newDialogTitle = this.makeDialogTitle(application);
            this.typedForm!.controls.event.controls.reserved.setValue(application.reserved);
            // ha betelt az éppen kiválasztott esemény, akkor jelezzük a felhasználónak
            if (application.reserved >= this.typedForm!.controls.event.controls.numberOfApplicants.value!) {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Esemény megtelt!',
                    detail: 'Az esemény által megadott létszám elérte a maximumot, kérjük válasszon egy másik időpontot!',
                    life: 3000,
                });
            }
        }
    }

    makeDialogTitle(newData: any) {
        const splitTitle = this.dialog!.newDialogTitle?.split(' |')
        if (splitTitle!.length > 2) {
            return splitTitle!.slice(0, 2).join(' |').concat(' | ' + newData!.reserved + '/' + this.typedForm!.controls.event.controls.numberOfApplicants.value)
        } else {
            return this.dialog!.newDialogTitle!.concat(' | ' + newData!.reserved + '/' + this.typedForm!.controls.event.controls.numberOfApplicants.value)
        }
    }

    monthChanged(event: any) {
        // mindig csak az aktuális hónapot és környékét kérjük le a szeverről
        this.beginningOfMonth = dateFns.addWeeks(new Date(event.year, event.month-1, 1), -1);
        this.endOfMonth = dateFns.addWeeks(dateFns.endOfMonth(new Date(event.year, event.month-1, 1)), 1);
        this.currentYear = event.year
        this.readAvailableEventByDate();
    }

    applicationCanChanged() {
        if (isNotNullOrUndefined(this.typedForm?.controls.plannedStart.value) && isNotNullOrUndefined(this.typedForm?.controls.id.value)) {
            return coalesce(this.typedForm?.controls.event.controls.activity.controls.cancelHoursBeforeEvent.value, 48)! <= dateFns.differenceInHours(this.typedForm?.controls.plannedStart.value!, new Date())
        } else {
            return true;
        }
    }

    canApply(event: EventVO) {
        return dateFns.differenceInHours(event.start!, new Date()) >= coalesce(event.activity.cancelHoursBeforeEvent, 48)
    }

    deleteApplication() {
        if (isNotNullOrUndefined(this.editedId)) {
            this.confirmationService.confirm({
                target: undefined,
                message: 'Biztosan törölni szeretné a jelentkezését a kiválasztott eseményre?',
                header: 'Megerősítés szükséges!',
                closable: true,
                closeOnEscape: true,
                icon: 'pi pi-exclamation-triangle',
                rejectButtonProps: {
                    label: 'Mégse',
                    severity: 'secondary',
                    outlined: true,
                },
                acceptButtonProps: {
                    label: 'Törlés',
                },
                accept: () => {
                    this.applicationFacade.remove(this.editedId!);
                    this.dialog?.closeDialog();
                },
                reject: () => {

                },
            })
        }
    }
}
